import React, { useState, useRef } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";

// Import Custom Hooks
import { useAuthRedirect } from '../hooks/useAuthRedirect';
import { useScrollAndFocus } from '../hooks/useScrollAndFocus';
import { useSidebarData } from '../hooks/useSidebarData';
import { useChatManager } from '../hooks/useChatManager';

// Import Refactored UI Components
import ChatMessages from '../components/Chat/ChatMessages';
import ChatInput from '../components/Chat/ChatInput';
import ChatSidebar from '../components/Sidebar/ChatSidebar';
import GPT5Settings from '../components/Settings/GPT5Settings';

// Import Modals (Assuming these are already somewhat self-contained)
import AddDataSourceModal from '../components/AddDataSourceModal';
import AgentManagement from '../components/AgentManagement'; // Corrected import name/path
import SchemaViewer from '../components/SchemaViewer';

const ENABLE_AGENT_MANAGEMENT = false; // <-- Feature Flag

export default function ChatPage() {
    // --- State Management ---
    // Authentication & Redirection (Handled by Hook)
    const { isLoading: isAuthLoading, isAuthenticated } = useAuthRedirect();

    // Sidebar Data (Handled by Hook)
    const {
        dataSources,
        agents,
        collection,
        error: sidebarError,
        editError: sidebarEditError,
        deleteError: sidebarDeleteError,
        agentDeleteError: sidebarAgentDeleteError, // Get agent delete error
        isLoading: isSidebarLoading,
        handleDataSourceUpdate,
        handleAgentsUpdate,
        // Edit state and handlers
        editingDataSource,
        showEditDataSourceModal,
        handleEditDataSource,
        handleCloseEditModal,
        // DS Delete state and handlers
        deletingDataSourceId,
        showDeleteConfirm,
        handleDeleteDataSourceClick,
        handleConfirmDelete,
        handleCancelDelete,
        // Agent Delete state and handlers
        deletingAgentId,
        showAgentDeleteConfirm,
        handleDeleteAgentClick, // Need agent delete click handler
        handleConfirmAgentDelete,
        handleCancelAgentDelete,
    } = useSidebarData();

    // Chat Management (Handled by Hook) - Pass initial dataSources
    const {
        messages,
        // setMessages, // Only needed if modifying messages outside the hook
        inputMessage,
        setInputMessage,
        isProcessing,
        apiCalls,
        expandedApiCalls,
        error: chatError, // Renamed to avoid conflict with sidebarError
        handleSendMessage,
        resetConversation, // Keep resetConversation if needed externally or via button
        toggleApiCall,
        // GPT-5 parameters and functions
        reasoningEffort,
        verbosity,
        setReasoningEffort,
        setVerbosity,
        resetGPT5Parameters,
        isGPT5Enabled,
    } = useChatManager(dataSources, collection?.auth_headers || {}); // Pass dataSources and collection headers

    // Local UI State
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showAddDataSourceModal, setShowAddDataSourceModal] = useState(false);
    const [showAgentManagementModal, setShowAgentManagementModal] = useState(false);
    const [agentModalKey, setAgentModalKey] = useState(0); // Key to force remount
    const [showSchemaViewer, setShowSchemaViewer] = useState(false);
    const [selectedDataSource, setSelectedDataSource] = useState(null);

    // Handler to open modal and update key
    const handleOpenAgentModal = () => {
        setAgentModalKey(prevKey => prevKey + 1); // Increment key to force remount
        setShowAgentManagementModal(true);
    };

    // Handler to view schema for a database data source
    const handleViewSchema = (dataSource) => {
        console.log('=== Schema View Debug ===');
        console.log('Selected dataSource:', dataSource);
        console.log('DataSource ID:', dataSource.data_source_id || dataSource.DataSourceId);
        console.log('DataSource keys:', Object.keys(dataSource));
        setSelectedDataSource(dataSource);
        setShowSchemaViewer(true);
    };

    // --- Refs ---
    const endOfMessagesRef = useRef(null);
    const inputRef = useRef(null);

    // --- Effects ---
    // Scrolling and Input Focus (Handled by Hook)
    useScrollAndFocus(endOfMessagesRef, inputRef, messages);

    // --- Auth0 Data (Still needed for Modals/Tenant ID) ---
    const { user, getAccessTokenSilently } = useAuth0(); // Keep user/token access
    const { collectionId } = useParams(); // Keep collectionId

    // --- Render Logic ---
    if (isAuthLoading) {
        return <div className="flex justify-center items-center min-h-screen">Loading authentication...</div>;
    }

    // useAuthRedirect handles redirection, so if not authenticated, it will navigate away.
    // We might still want to render null or a placeholder briefly before navigation occurs.
    if (!isAuthenticated) {
        return null; // Or a minimal loading/redirecting message
    }

    // Calculate main content width based on sidebar state
    const mainContentWidthClass = sidebarOpen ? 'w-[calc(100%-18rem)]' : 'w-full'; // Adjusted for w-72 sidebar


    return (
        <div className="flex justify-center items-start min-h-screen bg-gray-100 pt-2">
            {/* Outer container */}
            <div className="w-full max-w-6xl bg-white rounded-lg shadow-md overflow-hidden">
                {/* Flex container for chat area and sidebar */}
                <div className="flex h-[calc(90vh-0.5rem)] relative"> {/* Use relative for absolute sidebar */}
                    
                    {/* Main Chat Area */}
                    <div className={`flex flex-col transition-all duration-300 ease-in-out ${mainContentWidthClass}`}>
                        {/* Display Chat-related errors */}
                         {chatError && (
                             <div className="p-3 m-4 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
                                 <strong>Chat Error:</strong> {chatError}
                                 {/* Optionally add a dismiss button */}
                             </div>
                         )}

                        {/* Message Display Area */}
                        <ChatMessages messages={messages} endOfMessagesRef={endOfMessagesRef} isProcessing={isProcessing} />

                        {/* Input Area */}
                        <ChatInput
                            inputMessage={inputMessage}
                            setInputMessage={setInputMessage}
                            handleSendMessage={handleSendMessage}
                            isProcessing={isProcessing}
                            inputRef={inputRef}
                        />
                    </div>

                    {/* Sidebar Area */}
                    <ChatSidebar
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                        dataSources={dataSources}
                        agents={agents}
                        apiCalls={apiCalls}
                        expandedApiCalls={expandedApiCalls}
                        toggleApiCall={toggleApiCall}
                        setShowAddDataSourceModal={setShowAddDataSourceModal}
                        handleOpenAgentModal={handleOpenAgentModal} // Pass the handler with the correct prop name
                        sidebarError={sidebarError} // Pass sidebar-specific error
                        isSidebarLoading={isSidebarLoading} // Pass sidebar loading state
                        handleEditDataSource={handleEditDataSource}
                        handleDeleteDataSourceClick={handleDeleteDataSourceClick} // Pass delete handler
                        handleViewSchema={handleViewSchema} // Pass schema view handler
                        enableAgentManagement={ENABLE_AGENT_MANAGEMENT} // <-- Pass flag down
                        // GPT-5 Settings props
                        reasoningEffort={reasoningEffort}
                        verbosity={verbosity}
                        setReasoningEffort={setReasoningEffort}
                        setVerbosity={setVerbosity}
                        resetGPT5Parameters={resetGPT5Parameters}
                        isGPT5Enabled={isGPT5Enabled}
                    />
                </div>
            </div>

            {/* Modals */}
            {/* Render Add/Edit DataSource Modal */}
            {(showAddDataSourceModal || showEditDataSourceModal) && user && collectionId && (
                <AddDataSourceModal
                    isOpen={showAddDataSourceModal || showEditDataSourceModal}
                    // Close handler depends on whether we were editing or adding
                    onClose={editingDataSource ? handleCloseEditModal : () => setShowAddDataSourceModal(false)}
                    tenantId={user['https://tenxer.ai/tenant_id']}
                    collectionId={collectionId}
                    agents={agents} // Pass agents needed for DB form
                    // Pass the DS object if we are editing, otherwise null
                    dataSourceToEdit={editingDataSource}
                    // onSuccess triggers a refresh for both add and edit
                    onSuccess={() => {
                        handleDataSourceUpdate();
                        // Modal closes itself internally on success now
                    }}
                    // TODO: Pass sidebarEditError to the modal if it's adapted to display it
                />
            )}

            {/* Conditionally render Agent Management Modal based on flag */}
            {ENABLE_AGENT_MANAGEMENT && showAgentManagementModal && user && (
                // Assuming AgentManagement uses ShadCN Dialog primitives internally or is wrapped
                <Dialog open={showAgentManagementModal} onOpenChange={setShowAgentManagementModal}>
                    <DialogContent className="sm:max-w-[650px]"> {/* Adjust width as needed */}
                        <DialogHeader>
                            <DialogTitle>Manage Agents</DialogTitle>
                            <DialogDescription>
                                Create new agents or view registration details for existing ones. Agents run securely to connect to your private data sources like databases.
                            </DialogDescription>
                        </DialogHeader>
                        {/* AgentManagement content now goes inside the DialogContent */}
                        <AgentManagement
                            tenantId={user['https://tenxer.ai/tenant_id']}
                            initialAgents={agents}
                            isLoading={isSidebarLoading}
                            onAgentsUpdate={() => {
                                handleAgentsUpdate();
                            }}
                            onClose={() => setShowAgentManagementModal(false)}
                            handleDeleteAgentClick={handleDeleteAgentClick} // Pass delete handler
                        />
                        {/* Footer is handled inside AgentManagement now */}
                    </DialogContent>
                </Dialog>
            )}

            {/* Delete Confirmation Dialog (using standard Dialog + Alert) */}
            <Dialog open={showDeleteConfirm} onOpenChange={(open) => !open && handleCancelDelete()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this data source? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-gray-700">
                            Data Source ID: <span className="font-mono break-all">{deletingDataSourceId}</span>
                        </p>
                        {sidebarDeleteError && (
                            <Alert variant="destructive" className="mt-4">
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>{sidebarDeleteError}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={handleCancelDelete}>Cancel</Button>
                        <Button variant="destructive" onClick={handleConfirmDelete}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Agent Delete Confirmation Dialog */}
            <Dialog open={showAgentDeleteConfirm} onOpenChange={(open) => !open && handleCancelAgentDelete()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Agent Deletion</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this agent? This will attempt to clean up associated IoT resources but cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-gray-700">
                            Agent ID: <span className="font-mono break-all">{deletingAgentId}</span>
                        </p>
                        {sidebarAgentDeleteError && (
                            <Alert variant="destructive" className="mt-4">
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>{sidebarAgentDeleteError}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={handleCancelAgentDelete}>Cancel</Button>
                        <Button variant="destructive" onClick={handleConfirmAgentDelete}>Delete Agent</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Schema Viewer Modal */}
            <Dialog open={showSchemaViewer} onOpenChange={setShowSchemaViewer}>
                <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Database Schema</DialogTitle>
                        <DialogDescription>
                            {selectedDataSource && `Viewing schema for ${selectedDataSource.name || selectedDataSource.db_name || 'database'}`}
                        </DialogDescription>
                    </DialogHeader>
                    {selectedDataSource && user && (
                        <SchemaViewer
                            tenantId={user['https://tenxer.ai/tenant_id']}
                            datasourceId={selectedDataSource.data_source_id || selectedDataSource.DataSourceId}
                            agentId={selectedDataSource.agent_id || selectedDataSource.AgentId}
                            dataSource={selectedDataSource}
                            getAccessTokenSilently={getAccessTokenSilently}
                            className="border-0 shadow-none"
                        />
                    )}
                </DialogContent>
            </Dialog>

        </div>
    );
}